import { Box, Button, Divider, IconButton, Typography } from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'
import { useState } from 'react'

import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded'
import EditNoteIcon from '@mui/icons-material/EditNote'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import { LogoutButton } from './AuthButtons'
import { SelectOrg } from './SelectOrg'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import usePermissions from '@/utils/hooks/users'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import NewPill from './NewPill'

interface NavLinkItem {
  label: string
  to: string
  show?: boolean
  isNew?: boolean
}

export default function MobileNav() {
  const { permissions, isLoading: permissionsLoading } = usePermissions()

  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  const { isLoading, user, token, orgs: userOrgs } = getCurrentUser()
  const { isOnboarding } = useOnboardingStatus()
  const { isAnnualSubscriber } = usePausedOrg()
  if (isLoading || permissionsLoading) {
    return null
  }

  const showUsers = permissions?.role === 'admin'

  const navSections: { label: string; links: NavLinkItem[] }[] = isOnboarding
    ? [
        {
          label: 'Onboarding',
          links: [{ label: 'Resume Onboarding', to: '/onboarding' }],
        },
        {
          label: 'Manage Organisation',
          links: [
            { label: 'Organisation Profile', to: '/profile/details' },
            { label: 'Users', to: '/profile/users', show: showUsers },
          ],
        },
      ]
    : [
        {
          label: 'Manage Organisation',
          links: [
            { label: 'Organisation Profile', to: '/profile/details' },
            { label: 'Accounts', to: '/profile/accounts', show: showUsers },
            { label: 'Users', to: '/profile/users', show: showUsers },
            { label: 'Settings', to: '/profile/settings', show: showUsers },
          ],
        },
        {
          label: 'Get Funding',
          links: [
            { label: 'Funding Requests', to: '/funding-requests' },
            { label: 'Funding Opportunities', to: '/match' },
            {
              label: 'Browse Database',
              to: '/search',
              show: isAnnualSubscriber,
              isNew: true,
            },
            { label: 'My Applications', to: '/applications' },
          ],
        },
      ]

  const isLinkActive = (link: string) => {
    const [path] = link.split('?')

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    )
  }

  if (menuOpen) {
    return (
      <Box
        component="nav"
        aria-label="Main navigation menu"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 100,
          backgroundColor: '#fff',
          pt: 4,
          pb: 8,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '90%',
            width: '100%',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Button
            disableRipple
            onClick={() => setMenuOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#1C3045',
              position: 'absolute',
              top: '2vh',
              right: '1vh',
            }}
            aria-label="Close menu"
          >
            <CloseRoundedIcon sx={{ color: '#1C3045', fontSize: '30px' }} />
          </Button>

          <NavLink to="/" style={{ textDecoration: 'none' }}>
            <img
              src={`/Fundsorter-h-black800px.png`}
              alt="Fundsorter Logo"
              style={{
                width: '70vw',
                height: 'auto',
                paddingBottom: '2vh',
                paddingTop: '8vh',
                cursor: 'pointer',
              }}
            />
          </NavLink>

          <Box sx={{ width: '80%', display: 'grid', gap: 2 }}>
            {navSections.map((section) => (
              <Box key={section.label}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#1C3045', mb: 1 }}
                >
                  {section.label}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {section.links
                    .filter((link) => link.show !== false)
                    .map((link) => (
                      <Button
                        key={link.to}
                        disableRipple
                        color="inherit"
                        component={NavLink}
                        to={link.to}
                        sx={{
                          color: isLinkActive(link.to) ? 'white' : '#1C3045',
                          backgroundColor: isLinkActive(link.to)
                            ? '#1C3045'
                            : 'transparent',
                          textTransform: 'none',
                          fontSize: '18px',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            bgcolor: isLinkActive(link.to)
                              ? '#162433'
                              : '#f5f5f5',
                          },
                        }}
                        onClick={() => setMenuOpen(false)}
                      >
                        {link.label}
                        {link.isNew && (
                          <>
                            {' '}
                            <NewPill inverted={isLinkActive(link.to)} />
                          </>
                        )}
                      </Button>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gap: 1, width: '80%' }}>
            <SelectOrg token={token} user={user} userOrgs={userOrgs} />
            <Button
              disableRipple
              color="inherit"
              component={NavLink}
              to="/feedback"
              sx={{
                color: '#1C3045',
                textTransform: 'none',
                fontSize: '18px',
              }}
              onClick={() => setMenuOpen(false)}
            >
              <ModeCommentOutlinedIcon sx={{ fontSize: '20px', mr: 1 }} />
              Feedback
            </Button>

            <Button
              disableRipple
              color="inherit"
              component={NavLink}
              to="/help"
              sx={{
                color: '#1C3045',
                textTransform: 'none',
                fontSize: '18px',
              }}
              onClick={() => setMenuOpen(false)}
            >
              <HelpOutlineIcon sx={{ fontSize: '20px', mr: 0.5 }} /> Help
            </Button>
            <LogoutButton />
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      component="nav"
      aria-label="Bottom navigation"
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
        width: '100vw',
        borderRadius: 10,
        bgcolor: '#1C3045',
        py: 1,
        alignItems: 'center',
        px: 2,
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 1400,
      }}
    >
      <IconButton
        color="inherit"
        component={NavLink}
        to="/funding-requests"
        aria-label="Funding Requests"
        sx={{
          color: '#1C3045',
          textDecoration:
            location.pathname === '/funding-requests' ? 'underline' : 'none',
          textTransform: 'none',
          fontSize: '8vw',
          flexBasis: 0,
        }}
      >
        <PaidOutlinedIcon fontSize="inherit" sx={{ color: 'white' }} />
      </IconButton>
      <IconButton
        color="inherit"
        component={NavLink}
        aria-label="Funding Opportunities"
        to="/match"
        sx={{
          color: '#1C3045',
          textDecoration: location.pathname === '/match' ? 'underline' : 'none',
          textTransform: 'none',
          fontSize: '8vw',
          flexBasis: 0,
        }}
      >
        <TravelExploreRoundedIcon fontSize="inherit" sx={{ color: 'white' }} />
      </IconButton>
      <IconButton
        color="inherit"
        disableRipple
        onClick={() => setMenuOpen(true)}
        sx={{
          borderRadius: 10,
          bgcolor: 'white',
          p: 2,
          flexBasis: 0,
          '&:hover': {
            bgcolor: 'white',
          },
        }}
        aria-label="Menu"
      >
        <MenuRoundedIcon sx={{ color: '#1C3045', fontSize: '35px' }} />
      </IconButton>
      <IconButton
        aria-label="Applications"
        color="inherit"
        component={NavLink}
        to="/applications"
        sx={{
          color: '#1C3045',
          textDecoration:
            location.pathname === '/applications' ? 'underline' : 'none',
          textTransform: 'none',
          fontSize: '8vw',
          flexBasis: 0,
        }}
      >
        <EditNoteIcon fontSize="inherit" sx={{ color: 'white' }} />
      </IconButton>
      <IconButton
        color="inherit"
        component={NavLink}
        to="/profile"
        aria-label="Organisation Profile"
        sx={{
          color: '#1C3045',
          textDecoration:
            location.pathname === '/profile' ? 'underline' : 'none',
          textTransform: 'none',
          fontSize: '8vw',
          flexBasis: 0,
        }}
      >
        <PersonOutlineOutlinedIcon fontSize="inherit" sx={{ color: 'white' }} />
      </IconButton>
    </Box>
  )
}
